import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Upload, CheckCircle, RotateCcw, AlertTriangle } from 'lucide-react';

import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import {
  Container,
  StickyHeader,
  Title,
  Card,
  DropZone,
  DropText,
  FileName,
  SummaryGrid,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  Footer,
  HistoryTable,
  SectionTitle,
} from '../../components/import/ImportStyles';
import {
  getPerformanceImportTemplate,
  validatePerformanceImport,
  executePerformanceImport,
} from '../../services/performanceImportService';

// Performans kurulum verisinin toplu yüklenmesi: tek çok sayfalı Excel
// (puanlama skalası, yetkinlikler, pozisyon yetkinlikleri, hedef şablonları,
// kişi hedefleri).
//
// Diğer import ekranlarından TEK FARKI: kaydetme adımı dosyayı YENİDEN gönderir
// (sunucu geçici dosya saklamaz). Bu yüzden seçilen File nesnesi state'te
// tutulur; `selectedFile` boşsa kaydet butonu çalışamaz.
function translateRowError(t, key) {
  return t(`responseErrors.${key}`, { defaultValue: t(key, { defaultValue: key }) });
}

export default function PerformanceImportPage() {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const fileInputRef = useRef(null);

  const sheets = useMemo(() => result?.sheets ?? [], [result]);
  // 360 formları: bir pozisyon satırı birden çok soru üretebildiği için satır sayıları soru sayısını anlatmaz —
  // sunucunun verdiği form/soru sayıları ayrı satırda gösterilir (yalnız bir şey değişecekse).
  const feedbackFormsSummary = useMemo(() => {
    const s = sheets.find((x) => x.sheet === 'feedbackForms');
    if (!s || typeof s.newForms !== 'number') return null;
    const counts = {
      newForms: s.newForms, updatedForms: s.updatedForms ?? 0, newQuestions: s.newQuestions ?? 0, updatedQuestions: s.updatedQuestions ?? 0,
    };
    return Object.values(counts).some((v) => v > 0) ? counts : null;
  }, [sheets]);
  const totalRows = result?.totalRows ?? 0;
  const validRows = result?.validRows ?? 0;
  const errorRows = result?.errorRows ?? 0;

  const issues = useMemo(
    () => sheets.flatMap((s) => [...(s.errors ?? []), ...(s.warnings ?? [])]),
    [sheets],
  );

  const handleDownloadTemplate = useCallback(async () => {
    const res = await getPerformanceImportTemplate();
    // 403/hata durumunda res.data gerçek bir Blob DEĞİL, EMPTY_RESPONSE gövdesidir.
    if (!(res?.data instanceof Blob) || res.data.size < 100) return;
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'flexbee-performans-kurulum-sablonu.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  const handleFileChosen = useCallback(async (file) => {
    if (!file || !file.name.endsWith('.xlsx')) return;
    setFileName(file.name);
    setSelectedFile(file);
    setSaved(null);
    setBusy(true);
    try {
      const res = await validatePerformanceImport(file);
      if (res?.data?.status) setResult(res.data.data);
    } catch {
      // api.js interceptor toast'ladı; finally busy'yi geri açsın diye yakalıyoruz.
    } finally {
      setBusy(false);
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    handleFileChosen(e.target.files?.[0]);
    e.target.value = ''; // aynı dosya tekrar seçilebilsin
  }, [handleFileChosen]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    handleFileChosen(e.dataTransfer.files?.[0]);
  }, [handleFileChosen, busy]);

  const handleSave = useCallback(async () => {
    if (!selectedFile) return;
    setBusy(true);
    try {
      const res = await executePerformanceImport(selectedFile);
      if (res?.data?.status) {
        setResult(res.data.data);
        setSaved(res.data.data.validRows ?? 0);
      }
    } catch {
      // Handled by interceptor
    } finally {
      setBusy(false);
    }
  }, [selectedFile]);

  const handleReset = useCallback(() => {
    setResult(null);
    setFileName('');
    setSelectedFile(null);
    setSaved(null);
  }, []);

  return (
    <Container>
      <StickyHeader>
        <Title>{t('performanceImport.title')}</Title>
      </StickyHeader>

      <Card>
        <SectionTitle>{t('performanceImport.howItWorks')}</SectionTitle>
        <p style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#667085', lineHeight: 1.6 }}>
          {t('performanceImport.description')}
        </p>
        <Footer>
          <Button variant="outline" onClick={handleDownloadTemplate}>
            <Download size={16} style={{ marginRight: 8 }} />
            {t('performanceImport.downloadTemplate')}
          </Button>
          <div />
        </Footer>
      </Card>

      <Card>
        <SectionTitle>{t('performanceImport.uploadFile')}</SectionTitle>

        <DropZone
          $dragOver={dragOver}
          onClick={() => { if (!busy) fileInputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={busy ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          <Upload size={32} color="#667085" />
          <DropText>
            {fileName ? <FileName>{fileName}</FileName> : t('performanceImport.dropzone')}
          </DropText>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            data-testid="performance-import-file"
            disabled={busy}
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />
        </DropZone>

        {result && (
          <>
            <SummaryGrid style={{ marginTop: 20 }}>
              <SummaryCard $color="#F8FAFC">
                <SummaryValue>{totalRows}</SummaryValue>
                <SummaryLabel>{t('performanceImport.totalRows')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $color="#F0FDF4">
                <SummaryValue>{validRows}</SummaryValue>
                <SummaryLabel>{t('performanceImport.willBeSaved')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $color="#FEF2F2">
                <SummaryValue>{errorRows}</SummaryValue>
                <SummaryLabel>{t('performanceImport.hasError')}</SummaryLabel>
              </SummaryCard>
            </SummaryGrid>

            <HistoryTable style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>{t('performanceImport.sheet')}</th>
                  <th>{t('performanceImport.totalRows')}</th>
                  <th>{t('performanceImport.newRows')}</th>
                  <th>{t('performanceImport.updateRows')}</th>
                  <th>{t('performanceImport.hasError')}</th>
                </tr>
              </thead>
              <tbody>
                {sheets.map((s) => (
                  <tr key={s.sheet}>
                    <td>{t(`performanceImport.sheets.${s.sheet}`)}</td>
                    <td>{s.totalRows}</td>
                    <td>{s.newRows}</td>
                    <td>{s.updateRows}</td>
                    <td>{s.errorRows}</td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>

            {feedbackFormsSummary && (
              <p data-testid="feedback-forms-summary" style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#344054', marginTop: 12 }}>
                {t('performanceImport.feedbackFormsSummary', feedbackFormsSummary)}
              </p>
            )}

            {issues.length > 0 && (
              <HistoryTable style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th>{t('performanceImport.sheet')}</th>
                    <th>{t('performanceImport.rowNumber')}</th>
                    <th>{t('performanceImport.column')}</th>
                    <th>{t('performanceImport.message')}</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((e, i) => (
                    <tr
                      key={`${e.sheet}-${e.row}-${e.column}-${i}`}
                      style={{ background: e.severity === 'warning' ? '#FFFBEB' : '#FEF2F2' }}
                    >
                      <td>{e.sheet}</td>
                      <td>{e.row > 0 ? e.row : '-'}</td>
                      <td>{e.column}</td>
                      <td>
                        {e.severity === 'warning' && (
                          <AlertTriangle
                            size={14}
                            color="#d97706"
                            style={{ verticalAlign: 'text-bottom', marginRight: 6 }}
                          />
                        )}
                        {translateRowError(t, e.message)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </HistoryTable>
            )}
          </>
        )}

        <Footer>
          <div>
            {saved !== null && (
              <Tooltip text={t('performanceImport.resetTooltip')}>
                <IconButton onClick={handleReset}>
                  <RotateCcw size={16} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          {saved === null && (
            <Button
              data-testid="performance-import-submit"
              disabled={validRows === 0 || busy || !selectedFile}
              onClick={handleSave}
            >
              <CheckCircle size={16} style={{ marginRight: 8 }} />
              {`${t('performanceImport.save')} (${validRows})`}
            </Button>
          )}
        </Footer>

        {saved !== null && (
          <p style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#667085', marginTop: 8 }}>
            <CheckCircle size={14} color="#16a34a" style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            {t('performanceImport.savedSummary', { count: saved })}
          </p>
        )}
      </Card>
    </Container>
  );
}
